import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const InvoiceTable = ({ onPreview, onEdit, onDelete }) => {
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  const invoiceData = [
    {
      id: "INV-2025-001",
      client: "AJIO",
      clientType: "online",
      amount: "₹45,680",
      dueAmount: "₹45,680",
      issueDate: "2025-01-20",
      dueDate: "2025-02-19",
      status: "sent",
      paymentStatus: "pending",
      items: 125,
      designs: ["Floral Print", "Abstract Design"],
      overdueDays: 0
    },
    {
      id: "INV-2025-002",
      client: "Fashion Hub Pvt Ltd",
      clientType: "offline",
      amount: "₹78,920",
      dueAmount: "₹39,460",
      issueDate: "2025-01-18",
      dueDate: "2025-02-17",
      status: "sent",
      paymentStatus: "partial",
      items: 200,
      designs: ["Geometric Pattern", "Vintage Style"],
      overdueDays: 0
    },
    {
      id: "INV-2025-003",
      client: "MYNTRA",
      clientType: "online",
      amount: "₹92,340",
      dueAmount: "₹92,340",
      issueDate: "2025-01-15",
      dueDate: "2025-02-14",
      status: "sent",
      paymentStatus: "overdue",
      items: 300,
      designs: ["Summer Collection", "Casual Wear"],
      overdueDays: 12
    },
    {
      id: "INV-2025-004",
      client: "Style Store",
      clientType: "offline",
      amount: "₹56,780",
      dueAmount: "₹0",
      issueDate: "2025-01-12",
      dueDate: "2025-02-11",
      status: "paid",
      paymentStatus: "paid",
      items: 150,
      designs: ["Ethnic Wear", "Traditional Print"],
      overdueDays: 0
    },
    {
      id: "INV-2025-005",
      client: "Trendy Wear Co.",
      clientType: "offline",
      amount: "₹34,560",
      dueAmount: "₹34,560",
      issueDate: "2025-01-25",
      dueDate: "2025-02-24",
      status: "draft",
      paymentStatus: "pending",
      items: 80,
      designs: ["Modern Print", "Youth Collection"],
      overdueDays: 0
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-success bg-success/10';
      case 'sent': return 'text-primary bg-primary/10';
      case 'overdue': return 'text-error bg-error/10';
      case 'draft': return 'text-muted-foreground bg-muted/50';
      case 'cancelled': return 'text-muted-foreground bg-muted/50';
      default: return 'text-muted-foreground bg-muted/50';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-success bg-success/10';
      case 'partial': return 'text-warning bg-warning/10';
      case 'pending': return 'text-muted-foreground bg-muted/50';
      case 'overdue': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted/50';
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedInvoices(invoiceData.map(invoice => invoice.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    } else {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header Actions */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-foreground">Invoices</h3>
            <span className="text-sm text-muted-foreground">
              {invoiceData.length} total invoices
            </span>
          </div>
          {selectedInvoices.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {selectedInvoices.length} selected
              </span>
              <Button variant="outline" size="sm" iconName="Mail" iconPosition="left">
                Send Bulk
              </Button>
              <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                Export
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={selectedInvoices.length === invoiceData.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4 font-medium text-foreground">Invoice ID</th>
              <th className="text-left p-4 font-medium text-foreground">Client</th>
              <th className="text-left p-4 font-medium text-foreground">Amount</th>
              <th className="text-left p-4 font-medium text-foreground">Due Amount</th>
              <th className="text-left p-4 font-medium text-foreground">Issue Date</th>
              <th className="text-left p-4 font-medium text-foreground">Due Date</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="text-left p-4 font-medium text-foreground">Payment</th>
              <th className="text-left p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoiceData.map((invoice) => (
              <tr key={invoice.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.includes(invoice.id)}
                    onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{invoice.id}</div>
                  <div className="text-sm text-muted-foreground">{invoice.items} items</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{invoice.client}</div>
                  <div className="flex items-center space-x-1 mt-1">
                    <Icon 
                      name={invoice.clientType === 'online' ? 'Globe' : 'Store'} 
                      size={12} 
                      className="text-muted-foreground" 
                    />
                    <span className="text-xs text-muted-foreground capitalize">
                      {invoice.clientType}
                    </span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="font-semibold text-foreground">{invoice.amount}</div>
                </td>
                <td className="p-4">
                  <div className={`font-semibold ${
                    invoice.dueAmount === '₹0' ? 'text-success' : 'text-error'
                  }`}>
                    {invoice.dueAmount}
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-foreground">{formatDate(invoice.issueDate)}</div>
                </td>
                <td className="p-4">
                  <div className={`${
                    invoice.overdueDays > 0 ? 'text-error font-medium' : 'text-foreground'
                  }`}>
                    {formatDate(invoice.dueDate)}
                    {invoice.overdueDays > 0 && (
                      <div className="text-xs text-error mt-1">
                        {invoice.overdueDays} days overdue
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                    {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(invoice.paymentStatus)}`}>
                    {invoice.paymentStatus.charAt(0).toUpperCase() + invoice.paymentStatus.slice(1)}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onPreview(invoice)}
                      iconName="Eye"
                      iconSize={16}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(invoice)}
                      iconName="Edit"
                      iconSize={16}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(invoice)}
                      iconName="Trash2"
                      iconSize={16}
                      className="text-error hover:text-error"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4 p-4">
        {invoiceData.map((invoice) => (
          <div key={invoice.id} className="border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedInvoices.includes(invoice.id)}
                  onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                  className="rounded border-border"
                />
                <div className="font-medium text-foreground">{invoice.id}</div>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onPreview(invoice)}
                  iconName="Eye"
                  iconSize={16}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(invoice)}
                  iconName="Edit"
                  iconSize={16}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Client:</span>
                <div className="font-medium text-foreground">{invoice.client}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Amount:</span>
                <div className="font-semibold text-foreground">{invoice.amount}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Due Amount:</span>
                <div className={`font-semibold ${
                  invoice.dueAmount === '₹0' ? 'text-success' : 'text-error'
                }`}>
                  {invoice.dueAmount}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Due Date:</span>
                <div className={`${
                  invoice.overdueDays > 0 ? 'text-error font-medium' : 'text-foreground'
                }`}>
                  {formatDate(invoice.dueDate)}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(invoice.paymentStatus)}`}>
                  {invoice.paymentStatus.charAt(0).toUpperCase() + invoice.paymentStatus.slice(1)}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {invoice.items} items
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InvoiceTable;